import React, { useState } from 'react';
import { useGetPreparedCartsQuery, useGetPreparedCartsForStorefrontQuery } from '../../../store/endpoints/PreparedCartEndpoints';
import { useGetStorefrontsQuery } from '../../../store/endpoints/StorefrontSettingsEndpoints';
import { IPreparedCart } from '../../../models/PreparedCart';
import { PreparedCartPanel } from './PreparedCartPanel';
import GridComposer from '../../GridComposer/GridComposer';
import { GridSelectionMode, IColumnDetails } from '../../../models/GridConfiguration';
import { PrimaryButton } from '@fluentui/react';
import CompanyNameDisplay from '../../Contacts/CompanyNameDisplay';

interface PreparedCartsTableProps {
    storefront?: string;
}

const PreparedCartsTable: React.FC<PreparedCartsTableProps> = ({ storefront }) => {
    const [selectedCartId, setSelectedCartId] = useState<string | undefined>(undefined);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(25);
    const [filter, setFilter] = useState<string>();
    const [filterField, setFilterField] = useState<string>();
    const [sortBy, setSortBy] = useState<string>();
    const [sortDirection, setSortDirection] = useState<string>();

    const conditions = { page, pageSize, searchTerm: filter, searchBy: filterField, sortBy, sortDirection };
    const globalCarts = useGetPreparedCartsQuery({ conditions }, { skip: !!storefront });
    const scopedCarts = useGetPreparedCartsForStorefrontQuery({ storefrontId: storefront ?? '', conditions }, { skip: !storefront });
    const carts = storefront ? scopedCarts : globalCarts;
    const storefronts = useGetStorefrontsQuery();

    const getCustomColumns = (): IColumnDetails[] => [
        {
            index: 0,
            identifier: 'Details',
            label: '',
            type: '',
            component: () => <PrimaryButton>Details</PrimaryButton>,
            onClick: (cart: IPreparedCart) => setSelectedCartId(cart.id),
            required: true,
        },
        ...(!storefront ? [{
            identifier: 'storefrontId.keyword',
            label: 'Storefront',
            type: 'Reference',
            sortable: false,
            filterable: true,
            component: (cart: IPreparedCart) => (
                <div>{storefronts.data?.items?.find(s => s.id === cart.storefrontId)?.name ?? cart.storefrontId}</div>
            ),
            customFilterOptions: () =>
                storefronts.data?.items?.map(s => ({ key: s.id ?? '', text: s.name ?? '' })),
        } as IColumnDetails] : []),
        {
            identifier: 'targetCompany.keyword',
            label: 'Company',
            type: 'Reference',
            sortable: true,
            filterable: true,
            component: (cart: IPreparedCart) => <CompanyNameDisplay companyId={cart.targetCompany} />,
        },
        {
            identifier: 'delivered',
            label: 'Delivered',
            type: '',
            component: (cart: IPreparedCart) => (
                <span style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: cart.delivered ? '#dff6dd' : '#f3f2f1',
                    color: cart.delivered ? '#107c10' : '#605e5c',
                }}>
                    {cart.delivered ? 'Delivered' : 'Not Delivered'}
                </span>
            ),
        },
        {
            identifier: 'ordered',
            label: 'Ordered',
            type: '',
            component: (cart: IPreparedCart) => cart.ordered ? (
                <span style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: '#deecf9',
                    color: '#0078d4',
                }}>
                    Ordered
                </span>
            ) : <span />,
        },
    ];

    return (
        <div style={{ overflowX: 'auto' }}>
            <GridComposer
                data={carts.data ?? {}}
                page={page}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                loading={carts.isLoading || carts.isFetching}
                onRefresh={() => carts.refetch()}
                selectionMode={GridSelectionMode.multiple}
                columnSetName={'PreparedCart'}
                filter={filter}
                filterField={filterField}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={(column, direction) => {
                    setSortBy(column);
                    setSortDirection(direction);
                }}
                onFilter={(f, column) => {
                    setFilter(f);
                    setFilterField(column);
                }}
                overrideColumns={getCustomColumns()}
                canDragColumns={true}
                canResizeColumns={true}
                canCustomizeColumns={true}
                canChangePageSize={true}
                canCreateCsv={true}
                getItemId={(item: IPreparedCart) => item.id}
            />
            <PreparedCartPanel
                visible={selectedCartId !== undefined}
                workspaceId={selectedCartId ?? ''}
                onClose={() => setSelectedCartId(undefined)}
            />
        </div>
    );
};

export default PreparedCartsTable;
